import { ReactNode } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useAdminCapabilities } from "@/hooks/admin/useAdminCapabilities";
import { useTranslation } from "react-i18next";
import { getAdminHomePath, type AdminScope } from "@/lib/admin/adminScope";

interface AdminScopeRouteProps {
  allowedScopes: AdminScope[];
  children: ReactNode;
}

/**
 * UI-level scope guard for admin pages.
 * Keeps backward compatibility: users without scope metadata default to "super".
 * Scopes without dashboard access (e.g. VA) are redirected from /admin to their home.
 */
export function AdminScopeRoute({ allowedScopes, children }: AdminScopeRouteProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { scope, hasScope } = useAdminCapabilities();
  const homePath = getAdminHomePath(scope);

  if (!hasScope(allowedScopes)) {
    // Soft-redirect when hitting /admin without dashboard permission (VA, fleet, …)
    if (location.pathname === "/admin" && homePath !== "/admin") {
      return <Navigate to={homePath} replace />;
    }

    return (
      <div className="min-h-[40vh] flex items-center justify-center p-6">
        <div className="max-w-lg w-full border border-amber-200 bg-amber-50 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
            <div>
              <h2 className="text-base font-semibold text-amber-900">Accès restreint</h2>
              <p className="text-sm text-amber-800 mt-1">
                Votre profil admin n'a pas les permissions nécessaires pour cette section.
              </p>
              <Link
                to={homePath}
                className="inline-block mt-3 text-sm text-amber-900 underline hover:text-amber-700"
              >
                {t("common.backToAdminDashboard")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
